// Helper function to clean a device object for postMessage payload --
// this is where we remove our frontend helper properties from payload
export const cleanDeviceForPostMessage = (device) => {
  if (!device) return null;

  const {
    // exclude these
    role,
    suiteNumber: _suiteNumber,
    is_certificate_locked,
    screenId: _screenId,
    screen_name: _screen_name,
    screen_number: _screen_number,
    is_unmapped: _is_unmapped,
    suites,
    ip_uuid: _ip_uuid,
    valid_till: _valid_till,
    ...restOfData
  } = device;

  const cleanedSuites = (suites || []).map((suite) => ({
    created_at: suite.created_at,
    effective_from: suite.effective_from || null,
    suite_number: suite.suite_number,
    device_role: suite.device_role,
  }));

  const cleanedPayload = {
    ...restOfData,
    device_role: role?.code || device.device_role,
    suites: cleanedSuites,
  };

  // Only add is_certificate_locked if it's not null
  if (
    is_certificate_locked !== null &&
    typeof is_certificate_locked === 'boolean'
  ) {
    cleanedPayload.is_certificate_locked = is_certificate_locked;
  }

  return cleanedPayload;
};

export const updateDeviceInList = (screenDevices, deviceId, updatedDevice) => {
  let deviceFound = false;
  const updatedDevices = screenDevices.map((d) => {
    if (
      d.uuid === deviceId &&
      d.suites.find(
        (s) => s.suite_number === updatedDevice.suites[0].suite_number,
      )
    ) {
      deviceFound = true;
      return { ...d, ...updatedDevice };
    }
    return d;
  });
  return { updatedDevices, deviceFound };
};

// Helper functions for device management
export const filterDuplicateDevices = (devicesToRemoveFrom, devicesToMatch) => {
  const matchKeys = new Set(
    devicesToMatch.map((d) => `${d.device_model_id}_${d.serial_number}`),
  );
  return devicesToRemoveFrom.filter(
    (device) =>
      !matchKeys.has(`${device.device_model_id}_${device.serial_number}`),
  );
};

// Helper functions for device deletion
export const collectIpUuidsFromDevice = (device, suite) => {
  const ipUuids = [];

  // Check if deleting a specific suite or the whole device
  if (device.suites?.length > 1 && suite) {
    const selectedSuite = device.suites.find(
      (s) => Number(s.suite_number) === Number(suite),
    );
    if (selectedSuite?.ip_uuid) {
      ipUuids.push(selectedSuite.ip_uuid);
    }
  } else {
    // Removing the whole device - collect IPs
    (device.suites || []).forEach((s) => {
      if (s.ip_uuid) ipUuids.push(s.ip_uuid);
    });
    // Use ip_uuid for unmapped devices
    if (!device.suites?.length && device.ip_uuid) {
      ipUuids.push(device.ip_uuid);
    }
  }

  return ipUuids;
};

export const removeDeviceFromScreen = (screenDevices, deviceId, suite) => {
  let deviceFound = false;
  const updatedDevices = screenDevices.filter((d) => {
    // Remove unmapped device (no suites) if suite is not provided
    if (d.uuid === deviceId && (!suite || suite === null)) {
      if (!d.suites || d.suites.length === 0) {
        deviceFound = true;
        return false; // Remove this unmapped device
      }
      return true; // Keep mapped devices
    }
    // Remove suite from device if suite is provided and device has that suite
    if (
      d.uuid === deviceId &&
      d.suites?.some((s) => s.suite_number === suite)
    ) {
      deviceFound = true;
      if (d.suites.length > 1) {
        d.suites = d.suites.filter(
          (s) => Number(s.suite_number) !== Number(suite),
        );
        return true; // Keep device, but with suite removed
      }
      return false; // Remove device if this was the only suite
    }
    return true; // Keep all other devices
  });

  return { updatedDevices, deviceFound };
};

/**
 * Unified function to handle IP assignments and status updates
 * @param {Object} params
 * @param {Object} params.device - The device to assign IP to
 * @param {string} params.ipUuid - The UUID of the IP to assign
 * @param {Object} params.ipDetails - The IP details object containing device_ip, subnet_mask, gateway
 * @param {Object} params.screen - The screen object containing ip_details array
 * @param {string} [params.suiteNumber] - Optional suite number if this is a suite assignment
 * @param {boolean} [params.isAssigning=true] - Whether we're assigning or unassigning the IP
 * @returns {Object} Object containing updated device and screen data
 */
export const modifyIpForDevice = ({
  device,
  ipUuid,
  ipDetails = {},
  screen,
  suiteNumber = null,
  isAssigning = true,
}) => {
  if (!device || !screen) {
    console.error('[modifyIpForDevice] Missing required parameters');
    return { device, screen };
  }

  const updatedDevice = { ...device };
  const updatedScreen = { ...screen };
  const updatedIpDetails = [...(screen.ip_details || [])];

  const ipFields = ['ip_uuid', 'device_ip', 'subnet_mask', 'gateway'];

  const suiteIndex = (updatedDevice.suites || []).findIndex(
    (s) => Number(s.suite_number) === Number(suiteNumber),
  );

  if (isAssigning && ipUuid) {
    // Unassign existing assignment (if any)
    const existingAssignmentIndex = updatedIpDetails.findIndex(
      (ip) => ip.uuid === ipUuid && ip.is_assigned,
    );
    if (existingAssignmentIndex !== -1) {
      updatedIpDetails[existingAssignmentIndex] = {
        ...updatedIpDetails[existingAssignmentIndex],
        is_assigned: false,
        device_id: null,
        suite_number: null,
      };
    }

    // Assign IP to device
    Object.assign(updatedDevice, {
      ip_uuid: ipUuid,
      device_ip: ipDetails.device_ip,
      subnet_mask: ipDetails.subnet_mask,
      gateway: ipDetails.gateway,
    });

    if (suiteIndex !== -1) {
      updatedDevice.suites[suiteIndex] = {
        ...updatedDevice.suites[suiteIndex],
        ip_uuid: ipUuid,
        device_ip: ipDetails.device_ip,
        subnet_mask: ipDetails.subnet_mask,
        gateway: ipDetails.gateway,
      };
    }

    const ipIndex = updatedIpDetails.findIndex((ip) => ip.uuid === ipUuid);
    if (ipIndex !== -1) {
      updatedIpDetails[ipIndex] = {
        ...updatedIpDetails[ipIndex],
        is_assigned: true,
        device_id: device.uuid,
        suite_number: suiteNumber,
      };
    }
  } else {
    // Unassign IP
    ipFields.forEach((key) => (updatedDevice[key] = null));

    if (suiteIndex !== -1) {
      updatedDevice.suites[suiteIndex] = {
        ...updatedDevice.suites[suiteIndex],
        ip_uuid: null,
        device_ip: null,
        subnet_mask: null,
        gateway: null,
      };
    }

    const ipIndex = updatedIpDetails.findIndex(
      (ip) =>
        ip.uuid === ipUuid ||
        (ip.device_id === device.uuid && ip.suite_number === suiteNumber),
    );
    if (ipIndex !== -1) {
      updatedIpDetails[ipIndex] = {
        ...updatedIpDetails[ipIndex],
        is_assigned: false,
        device_id: null,
        suite_number: null,
      };
    }
  }

  updatedScreen.ip_details = updatedIpDetails;

  return { device: updatedDevice, screen: updatedScreen };
};

/**
 * Handles moving a device (and unassigning IPs) from a source screen (and suite) to a target screen (or standby).
 * Returns a cleaned device object ready for the new location, or null if duplicate exists in target.
 */
export const handleDeviceMovement = ({
  device,
  sourceScreen,
  targetScreen,
  suiteNumber = null,
}) => {
  if (!device || !device.uuid) {
    console.error('[handleDeviceMovement] Invalid device data:', device);
    return null;
  }

  let movedDevice = null;
  let ipUuidsToUnassign = new Set();

  // 1. Find and remove device from source screen
  if (sourceScreen) {
    const deviceIdx = sourceScreen.devices.findIndex(
      (d) =>
        d.uuid === device.uuid &&
        (suiteNumber
          ? d.suites?.some((s) => s.suite_number === suiteNumber)
          : true),
    );
    if (deviceIdx !== -1) {
      movedDevice = { ...sourceScreen.devices[deviceIdx] };
      // Remove from source
      sourceScreen.devices.splice(deviceIdx, 1);
      // Collect IPs to unassign
      if (movedDevice.ip_uuid) ipUuidsToUnassign.add(movedDevice.ip_uuid);
      (movedDevice.suites || []).forEach((suite) => {
        if (suite.ip_uuid) ipUuidsToUnassign.add(suite.ip_uuid);
      });
      // Unassign IPs in source screen
      if (ipUuidsToUnassign.size > 0) {
        const { screen: updatedSourceScreen } = modifyIpForDevice({
          device: movedDevice,
          ipUuid: null,
          ipDetails: {},
          screen: sourceScreen,
          suiteNumber,
          isAssigning: false,
        });
        Object.assign(sourceScreen, updatedSourceScreen);
      }
    }
  }

  // 2. If not found in source, fallback to passed device
  if (!movedDevice) {
    movedDevice = { ...device };
  }

  // 3. Prevent duplicate in target (if targetScreen is provided)
  if (targetScreen) {
    const existsInTarget = targetScreen.devices?.some(
      (d) =>
        d.uuid === movedDevice.uuid && (!d.suites || d.suites.length === 0),
    );
    if (existsInTarget) return null;
  }

  // 4. Return cleaned device object (unmapped, IPs cleared, suites empty)
  const cleanedDevice = {
    ...movedDevice,
    ip_uuid: null,
    device_ip: null,
    subnet_mask: null,
    gateway: null,
    suites: [],
    is_unmapped: true,
    software_version: movedDevice.software_version || null,
  };

  return {
    movedDeviceData: cleanedDevice,
    ipUuidsToHandle: Array.from(ipUuidsToUnassign),
  };
};
